// Definitions
// ------------

export const EMPTY_STRING = "";
export const HTML_NEWLINE = "<br>";

// Constant HREFS
export const NIMIS_WEBSITE_URL = "https://nimisintelligence.com";

// URLs
export const BASEURL = ".nimisintelligence.com";
export const COGNITO_MAP_SUFFIX = "/map.json";
export const API_URL_SUFFIX = "/graphql";
export const API_URL_PREFIX = "api-";
export const HTTPS = "https://";
export const HTTP = "http://";
export const WWW_CLIENT = "www";
export const FORGOTPASSPHRASE_SUFFIX = "/forgot";

// Pages
// Login pages
export const LOGIN_PAGE = "/";
export const FORGOT_PASSWORD_PAGE = "/forgot";
export const ACTIVATE_ACCOUNT_PAGE = "/activate";
export const UPDATE_PASSPHRASE_PAGE = "/update";
export const RESET_PASSPHRASE_PAGE = "/reset";
export const NEW_USER_LOGIN_PAGE = "/welcome";
export const REQUEST_INVITE_PAGE = "/request-invite";
export const MFA_CHALLENGE_PAGE = "/mfa";
export const LOG_OUT_PAGE = "/"; // TODO: Is using the same page for login/logout ok?

// Non login pages
export const DASHBOARD_PAGE = "/dashboard";
export const MANAGE_APPS_PAGE = "/applications";
export const SINGLE_WEBAPP_PAGE = "/webapp/:webappid";
export const MANAGE_USERS_PAGE = "/manageusers";
export const ADD_APP_PAGE = "/addapplication";
export const USER_PAGE = "/user";
export const INTEGRATIONS_PAGE = "/integrations";
export const BILLING_PAGE = "/account/billing";
export const VULNERABILITY_PAGE =
  "/webapp/:webappid/tests/:testid/vulnerability";

// Input validation
export const EMAIL_FORMAT_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const INVALID_EMAIL_FORMAT_ERROR = "Please enter a valid email address";

// Avatar upload — accepted formats + the 5 MB limit (enforced server-side by the
// ImageUploadValidator Lambda). Shown wherever an avatar can be changed so the
// user knows the constraint up front.
export const AVATAR_UPLOAD_HINT = "PNG, JPEG, GIF or WebP · up to 5 MB";

// Cognito account status. A user holding a temporary password sits in
// FORCE_CHANGE_PASSWORD until they complete the invite; the dashboard shows
// that as a pending invite. Mirrors COGNITO_FORCE_CHANGE_PASSWORD_STATUS in
// handlers/userdatasource.py.
export const COGNITO_STATUS_INVITE_PENDING = "FORCE_CHANGE_PASSWORD";

// Backend sentinel codes
// -----------------------
// Handlers return {"message": CODE} for outcomes that cannot be expressed in a
// field's return type (a Boolean has no value meaning "we did not answer"), and
// the response resolver renders them as a GraphQL error. These are STABLE CODES,
// not display text — map them to user-facing copy at the call site.
// Mirrors models/definitions.py.
export const BACKEND_CODE_RATE_LIMITED = "RATE_LIMITED";
export const BACKEND_CODE_INTERNAL_ERROR = "INTERNAL_ERROR";
export const BACKEND_CODE_INVALID_DISPLAY_NAME = "INVALID_DISPLAY_NAME";

// Preferred username (Cognito preferred_username) — one term, used everywhere.
// The field was previously labelled "Preferred Username" while the page called
// the same value "Display Name".
export const PREFERRED_USERNAME_LABEL = "Preferred username";

// Input length limits
export const MAX_LENGTH_APP_NAME             = 50;
export const MAX_LENGTH_APP_URL              = 250;
export const MAX_LENGTH_USERNAME             = 100;
export const MAX_LENGTH_PASSPHRASE           = 200;
// Base32-encoded TOTP shared secret (authenticator seed). Real seeds are
// typically 16–64 base32 chars; 64 leaves headroom for spaces/formatting a
// user may paste from a provisioning string. NOT the dashboard user's own
// Cognito MFA — this is the target app's test-account authenticator secret.
export const MAX_LENGTH_TOTP_SECRET          = 64;
export const MAX_LENGTH_LINK_SCOPE           = 500;
export const MAX_LENGTH_API_DOMAINS          = 500;
export const MAX_LENGTH_SECURITY_HEADER_NAME = 100;
export const MAX_LENGTH_SECURITY_HEADER_VALUE = 500;

// Allowed characters for an application name, mirrored in ProjectProperties'
// APP_NAME_WHITELIST_CHARS validation. Surfaced to the user as a field hint so
// the rule is stated up front rather than only on a rejected blur.
export const APP_NAME_ALLOWED_CHARS_HINT =
  "Letters, numbers, spaces, and hyphens only.";

// Minutes a test must run before a manual stop counts as one of the app's
// tests (the "billable" threshold). The source of truth is the backend
// PlanCatalog value (billableMinDurationSeconds, default 25 min), which is
// tunable server-side without a deploy — keep this in step if that value is
// ever changed.
//
// Deliberately NOT shown to the customer anywhere: it is used only to decide
// whether the Stop Test dialog may promise that stopping now is free. The
// backend remains the authority on what is actually charged.
export const BILLABLE_TEST_MIN_MINUTES = 25;

export const SECONDS_PER_MINUTE = 60;

// Cognito Login
export const NEW_PASSWORD_REQUIRED = "NEW_PASSWORD_REQUIRED";
export const PASSWORD_RESET_REQUIRED_ERROR = "PasswordResetRequiredException";
// Cognito MFA challenge names (returned by Auth.signIn / Auth.completeNewPassword
// as `challengeName`). SOFTWARE_TOKEN_MFA = returning user with TOTP enrolled must
// enter a 6-digit code; MFA_SETUP = user has no MFA yet and must enrol TOTP now
// (issued to every user while the pool is MfaConfiguration=REQUIRED).
export const SOFTWARE_TOKEN_MFA = "SOFTWARE_TOKEN_MFA";
export const MFA_SETUP = "MFA_SETUP";
// Issuer label shown in the user's authenticator app entry (otpauth `issuer`).
export const MFA_TOTP_ISSUER = "NIMIS Intelligence";

// Groups - this comes straight from the backend code and is replicated in python and the cloudformation template for auth
export const CLIENT_OWNER_GROUP = "ClientOwnerGroup";
export const ADMIN_GROUP = "AdminGroup";
export const PENTESTER_GROUP = "PentesterGroup";
// Friendly role names
export const CLIENT_OWNER_DISPLAY_VALUE = "Account Owner";
export const ADMIN_DISPLAY_VALUE = "Administrator";
export const PENTESTER_DISPLAY_VALUE = "Pentester";

// One-line capability summaries per role. Surfaced wherever a role is chosen
// or explained (the Users role legend + the change-role buttons) so the copy
// stays consistent. Capabilities mirror the route/menu gating: Pentester =
// run tests; Administrator adds user/app/integration management (RoleGate);
// Account Owner adds billing + ownership transfer (OwnerGate).
export const PENTESTER_ROLE_DESCRIPTION = "View applications and run tests.";
export const ADMIN_ROLE_DESCRIPTION =
  "Everything a Pentester can do, plus manage users, application settings, and integrations.";
export const CLIENT_OWNER_ROLE_DESCRIPTION =
  "Full control, including billing. One per account — only the Account Owner can transfer ownership.";

// Test Phases - Front End
// These must match the backend TestPhase enum names after underscore-to-space conversion.
//
// v1 vuln-state-model rework: the active phases for new records are
// No_Tests, No_Findings, and Results_Ready. The legacy values
// (Live_Tests / Remediation_In_Progress / All_Fixed / Unknown) remain
// for historical readback of older WebAppTestStatus rows but should
// not be emitted by new code paths. See the v1 brief §3.3.
export const UNKNOWN_PHASE_VALUE = "Unknown";
export const NO_TESTS_PHASE_VALUE = "No Tests";
export const NO_FINDINGS_PHASE_VALUE = "No Findings";
export const LIVE_TESTS_PHASE_VALUE = "Test Live";
export const RESULTS_READY_PHASE_VALUE = "Results Ready";
export const REMEDIATION_IN_PROGRESS_PHASE_VALUE = "Remediation In Progress";
export const ALL_FIXED_PHASE_VALUE = "All Fixed";
// Emitted by the BE when the latest terminal test for an app is
// Failed/Invalid (Cancelled, InfrastructureFailure, ServerUnreachable).
// Pre-defined here so the FE pill style is wired before the BE deploys
// the corresponding enum value — see ~/Desktop/FailedTestFindings_BEBrief.md.
export const TEST_FAILED_PHASE_VALUE = "Test Failed";

// Button directions
export const BUTTON_NEXT = "next";
export const BUTTON_PREV = "previous";

// Misc
export const ONE_HUNDRED_PERCENT = 100;

// Single web app display time period
export const DISPLAY_PAST_3_MONTHS = "Last 3 Months";
export const DISPLAY_PAST_6_MONTHS = "Last 6 months";
export const DISPLAY_PAST_12_MONTHS = "Last 12 Months";
export const DISPLAY_PAST_ALL_TIME = "All Time";

// Verification tags
export const VALIDATION_PREFIX_STRING = '<meta name="validation" content="';
export const VALIDATION_SUFFIX_STRING = '">';
// Ownership verification (HTTP-layer; DNS TXT removed). The web app proves
// ownership with a <meta name="nimis-verify" content="<token>"> tag on the page
// at its URL; each API host+path proves ownership by serving the same token at
// GET https://<host>/<base>/nimis-verify (text/plain).
export const VERIFICATION_META_TAG_NAME = "nimis-verify";
export const NIMIS_VERIFY_PATH_SEGMENT = "nimis-verify";

// Enums
// -------
// Definitions come from the appsync graph schema

// Risk Rating
export const RISK_RATING_CRITICAL = "Critical";
export const RISK_RATING_HIGH = "High";
export const RISK_RATING_MED = "Medium";
export const RISK_RATING_LOW = "Low";
export const RISK_RATING_INFO = "Information";
export const RISK_RATING_UNCLASSIFIED = "Unclassified";
export const ALL_RISKS = [
  RISK_RATING_UNCLASSIFIED,
  RISK_RATING_LOW,
  RISK_RATING_MED,
  RISK_RATING_HIGH,
  RISK_RATING_CRITICAL,
];

// TestFrequency
export const TEST_FREQUENCY_WEEKLY = "Weekly";
export const TEST_FREQUENCY_FORTNIGHTLY = "Fortnightly";
export const TEST_FREQUENCY_MONTHLY = "Monthly";
export const TEST_FREQUENCY_NONE = "Not Defined";

// Vuln Status
// These are the UI-layer values (spaces, matching what convertFromGraphAPItoUI produces).
// convertFromUIToGraphAPI converts them back to underscore format before sending to AppSync.
export const VULN_STATUS_OPEN = "Open";
export const VULN_STATUS_RISK_ACCEPTED = "Risk Accepted";
export const VULN_STATUS_FIXED = "Fixed";
export const VULN_STATUS_NONE = "Not Defined";
export const ALL_VULN_STATUS = [
  VULN_STATUS_NONE,
  VULN_STATUS_FIXED,
  VULN_STATUS_RISK_ACCEPTED,
  VULN_STATUS_OPEN,
];

// Vuln Names
export const VULN_NAME_CSRF = "Cross Site Request Forgery";
export const VULN_NAME_BLIND_SQLI = "Blind SQL Injection";
export const VULN_NAME_UNION_SQLI = "Union SQL Injection";
export const VULN_NAME_DOM_XSS = "DOM Cross Site Scripting";
export const VULN_NAME_REFLECTED_XSS = "Reflected Cross Site Scripting";
export const VULN_NAME_STORED_XSS = "Stored Cross Site Scripting";
export const VULN_NAME_IDOR = "Insecure Direct Object Reference";
export const VULN_NAME_UNCLASSIFIED = "Unclassified";

export const ALL_VULN_NAMES = [
  VULN_NAME_UNCLASSIFIED,
  VULN_NAME_STORED_XSS,
  VULN_NAME_REFLECTED_XSS,
  VULN_NAME_DOM_XSS,
  VULN_NAME_UNION_SQLI,
  VULN_NAME_BLIND_SQLI,
  VULN_NAME_CSRF,
  VULN_NAME_IDOR,
];

// Vuln Category display names
// ----------------------------
// The backend stores categories as enum names (CSRF, SEC_HEADER_MISCONFIG,
// SQLi, etc — see DeepDiscovery/Shared/Vulnerabilities/VulnerabilityEnums.py).
// Some are acronyms that read fine as-is, others are snake_case constants
// that need expanding into full prose. This map is the single source of
// truth for category presentation across the app (vuln detail page,
// dashboard category breakdown, reports). Anything not in the map falls
// back to a sensible default (underscore → space, title case).
export const VULN_CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  Unclassified:           "Unclassified",
  CSRF:                   "Cross-Site Request Forgery",
  SQLi:                   "SQL Injection",
  XSS:                    "Cross-Site Scripting",
  IDOR:                   "Insecure Direct Object Reference",
  PATH_TRAVERSAL:         "Path Traversal",
  RCE:                    "Remote Code Execution",
  SEC_HEADER_MISCONFIG:   "Security Header Misconfiguration",
  PASSWORD_BRUTE_FORCE:   "Password Brute Force",
  BROKEN_ACCESS_CONTROL:  "Broken Access Control",
  DNS_SECURITY:           "DNS Security",
  TLS_SECURITY:           "TLS Security",
  SSRF:                   "Server-Side Request Forgery",
  OPEN_REDIRECT:          "Open Redirect",
  XXE:                    "XML External Entity",
  INFORMATION_DISCLOSURE: "Information Disclosure",
  OUTDATED_SOFTWARE:      "Outdated Software",
  SESSION_MANAGEMENT:     "Session Management",
  // Operational / aggregate categories the pentest container emits about
  // the run itself rather than about a specific weakness.
  SERVER_UNREACHABLE:     "Server Unreachable",
  RATE_LIMITED:           "Rate Limited",
  CAPTCHA_DETECTED:       "CAPTCHA Detected",
  TEST_STOPPED_EARLY:     "Test Stopped Early",
  HTTP_ONLY_PAGES:        "HTTP-Only Pages",
  MIXED_CONTENT_API_CALLS: "Mixed Content API Calls",
  // These lagged the container. Missing entries only cost a prettier
  // label — formatVulnCategory title-cases anything it doesn't know — but
  // the same drift in the BACKEND's mirrors is not survivable, so when one
  // of these appears here it should be mirrored in models/enums.py and the
  // GraphQL schema too. See models/vuln_taxonomy.py, which logs the gap.
  NOSQLI:                 "NoSQL Injection",
  MASS_ASSIGNMENT:        "Mass Assignment",
  JWT:                    "JSON Web Token",
  CORS:                   "Cross-Origin Resource Sharing",
  SSTI:                   "Server-Side Template Injection",
  SUBDOMAIN_TAKEOVER:     "Subdomain Takeover",
  BUSINESS_STATE_INTEGRITY: "Business State Integrity",
};

// Test status enum values, mirroring the backend TestStatus enum
// (schema/client_appsync.graphql). The backend now publishes Stopping
// during teardown — endTime / endTrigger / testStatusMessage are all set
// at the Stopping transition, but the test is still considered ongoing
// for UI purposes (Start button stays disabled, no new test can launch
// until terminal status arrives).
export const TEST_STATUS_PROVISIONING = "Provisioning";
export const TEST_STATUS_RUNNING = "Running";
export const TEST_STATUS_STOPPING = "Stopping";
export const TEST_STATUS_COMPLETED = "Completed";
export const TEST_STATUS_INVALID = "Invalid";
export const TEST_STATUS_FAILED = "Failed";

// Statuses where the test has fully resolved and no further activity is
// expected from the backend. testRunning flips to false only on these.
export const TERMINAL_TEST_STATUSES: ReadonlySet<string> = new Set([
  TEST_STATUS_COMPLETED,
  TEST_STATUS_INVALID,
  TEST_STATUS_FAILED,
]);

// Prefix on the locally-generated id that WebApplicationClass.startTest
// gives its optimiztic placeholder test, before the backend hands back the
// real testid. Any code that turns a testid into a backend call must skip
// ids carrying this prefix — the backend has never heard of them, so the
// call can only fail. Shared (not file-local) because both the writer
// (webApplication) and the readers (testResult fetches) need it.
export const OPTIMIZTIC_TEST_ID_PREFIX = "pending-";

// Statuses where the test is still in progress and findings are not
// yet final. Used to gate UI surfaces that should show a placeholder
// state ("Test in progress") rather than partial counts.
export const IN_FLIGHT_TEST_STATUSES: ReadonlySet<string> = new Set([
  TEST_STATUS_PROVISIONING,
  TEST_STATUS_RUNNING,
  TEST_STATUS_STOPPING,
]);

// "Live" means the test is still active for UI purposes — Start button
// stays disabled, vuln-table shows "Test in progress", report stays
// disabled. Stopping is treated as live: although endTime is set, the
// environment is still tearing down and the user must wait before a new
// test can be launched. No endTime + no testStatus ⇒ legacy/transient
// state, treat as live (matches the pre-Stopping behavior).
export function isTestLive(
  testStatus: string | null | undefined,
  endTime: string | number | null | undefined
): boolean {
  if (testStatus === TEST_STATUS_STOPPING) return true;
  return !endTime;
}

// Returns the human-readable display name for a vuln category. Accepts the
// raw backend enum name. Falls back to a snake_case → Title Case conversion
// for any future categories not yet in the map.
export function formatVulnCategory(rawCategory: string | null | undefined): string {
  if (!rawCategory) return "";
  const mapped = VULN_CATEGORY_DISPLAY_NAMES[rawCategory];
  if (mapped) return mapped;
  // Fallback: split on _, lowercase, then title-case each word.
  return rawCategory
    .split("_")
    .map((word) => (word.length > 0 ? word[0].toUpperCase() + word.slice(1).toLowerCase() : word))
    .join(" ");
}

// Vuln next, prev, close button names
export const NEXT_VULN_BUTTON_NAME = "Next";
export const PREV_VULN_BUTTON_NAME = "Prev";
export const CLOSE_VULN_BUTTON_NAME = "Close";

// Test request-rate presets. Users pick a simple Low / Medium / High
// tier rather than a bare number — ten near-identical numeric choices
// created decision overwhelm with no real benefit, and the backend
// already throttles automatically if the target application struggles.
// The stored value stays a numeric requests-per-second (the backend
// clamps to [10, 100]), so this is a UI-only change with no contract
// impact. Single source of truth so the Add Application wizard and the
// existing-app Settings modal stay in lockstep. Medium (20) is the
// conservative default — enough to make progress without hammering the
// customer's app on first run.
export interface RequestRateTier {
  readonly label: string;
  readonly requestsPerSecond: number;
}
export const REQUEST_RATE_TIERS: ReadonlyArray<RequestRateTier> = [
  { label: "Low", requestsPerSecond: 10 },
  { label: "Medium", requestsPerSecond: 20 },
  { label: "High", requestsPerSecond: 50 },
];
export const REQUESTS_PER_SECOND_DEFAULT = 20;

// Snap a stored requests-per-second to the closest tier value. Legacy
// apps may hold any of the old 10–100 values, and the numeric field is
// still writable via the API, so the dropdown must always resolve to a
// valid tier selection. Ties resolve to the lower tier (first match).
export function resolveRequestRateTierValue(storedRequestsPerSecond: number): number {
  const target =
    storedRequestsPerSecond > 0 ? storedRequestsPerSecond : REQUESTS_PER_SECOND_DEFAULT;
  return REQUEST_RATE_TIERS.reduce((closestValue, tier) => {
    const isCloser =
      Math.abs(tier.requestsPerSecond - target) < Math.abs(closestValue - target);
    return isCloser ? tier.requestsPerSecond : closestValue;
  }, REQUEST_RATE_TIERS[0].requestsPerSecond);
}

// App Settings
export const APP_SETTINGS_ACKNOWLEDGMENT_COMPONENT_NAME = "Acknowledgment";
export const APP_SETTINGS_PROJECTPROPERTIES_COMPONENT_NAME =
  "ProjectProperties";
export const APP_SETTINGS_PENTESTSCOPE_COMPONENT_NAME = "PentestScope";
export const APP_SETTINGS_APPSETTINGS_COMPONENT_NAME = "ApplicationSettings";
export const APP_SETTINGS_TESTACCOUNTS_COMPONENT_NAME = "TestAccounts";
export const APP_SETTINGS_VERIFYAPP_COMPONENT_NAME = "VerifyApplication";
export const APP_SETTINGS_SCHEDULETEST_NAME = "ScheduleTest";
export const APP_SETTINGS_FINISHED = "FINISHED";
export const HEADING_STEP_CURRENT = "current";
export const HEADING_STEP_PREVIOUS = "past";

// Local storage to determine when we first display the dashboard
export const DASHBOARD_DISPLAYED_KEY = "BDNRG";
export const DONT_DISPLAY_DASHBOARD_ANIMATION = '"false"';
export const DISPLAY_DASHBOARD_ANIMATION = '"true"';

// Converts epoch time that we receive from graphql into JS time
export const EPOCH_TO_JS_DATE_CONVERSION = 1000;

export enum CacheKey {
  getUser = "getUser",
  quickListWebApplications = "quickListWebApplications",
  listWebAppTestStatuses = "listWebAppTestStatuses",
  listLatestTestsForDashboard = "listLatestTestsForDashboard",
  listVulnHistories = "listVulnHistories",
  listIntegrations = "listIntegrations",
  listIntegrationCredentials = "listIntegrationCredentials",
  // Per-app license slots. Warmed at login after the dashboard's own
  // preloads so the Add-App license picker paints from cache instead of
  // blocking on a round trip. Invalidated by a purchase — see
  // ENTITLEMENTS_UPDATE in eventBus.service.
  listTenantEntitlements = "listTenantEntitlements",
  // Tenant-level facts (seat limit + tier label). Warmed at login alongside
  // the license slots: both are single small queries, and without a cache the
  // Users and Billing pages render a "no limit" phrasing for the instant
  // before the read lands, then visibly correct themselves.
  getTenantUsage = "getTenantUsage",
}
